import React, { useEffect, useRef, useState } from 'react';
import { HeartPulse, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
// La casilla NO manda a solas: si dice «no» y al lado hay una alergia escrita,
// se lee la alergia. Misma regla que el parte del día, el cuadrante y la hoja
// de anestesia (`backend/services/riesgo_clinico.py`, 2026-09-18/21).
import { AVISO_REVISAR, textoQueContradice } from '../../lib/riesgoClinico';

/**
 * Información clínica del paciente junto a los «Detalles» de la cita
 * (2026-09-03, dirección, punto 5), SOLO LECTURA. Se rellena sola al elegir el
 * paciente (o al abrir el resumen de la cita) y nunca bloquea guardar: si el
 * paciente aún no ha dado el dato (citas previas a la primera consulta) se
 * lee «Pendiente de cumplimentar».
 *
 * Dirección (2026-09-08, punto 9): fuera el apartado «Enfermedades»; en su
 * lugar, los cuatro datos clínicos de la ficha general del paciente
 * (pestaña Historia clínica → Datos clínicos), en este orden y sin teclear
 * nada en cada cita:
 *  - Alergias → `alergias` (null = sin preguntar, false = «No», true = el
 *    texto de `alergias_text`) Y lo escrito al lado, que manda sobre la
 *    casilla cuando se contradicen (2026-09-21, ver `textoAlergias`).
 *  - Medicación → `medicacion_habitual`.
 *  - Ácido hialurónico previo → `ah_previo` (null = sin preguntar, false =
 *    «No», true = el texto de `ah_previo_text`, «Sí» si no hay detalle), con
 *    la misma regla para lo escrito al lado.
 *  - Antecedentes médicos → `antecedentes_medicos` (diabetes, HTA, cirugías
 *    previas… y lo que vuelca el cuestionario de salud) Y NADA MÁS.
 *
 * Corrección de administración (2026-09-09): este apartado caía a la
 * `historia_general` y después a las notas de la importación cuando los
 * antecedentes estaban vacíos, con un sufijo de origen, y en el resumen de
 * cita se leía la historia general entera donde debían ir los antecedentes.
 * Ya no hay cascada: se enseña SOLO el campo `antecedentes_medicos` de la
 * ficha general y, si está vacío, «Pendiente de cumplimentar», igual que los
 * otros tres apartados. La historia general se lee donde le toca: la pestaña
 * Historia clínica de la ficha (y los informes, que la sacan aparte con su
 * propio título).
 *
 * Todo sale de GET /api/patients/{id} (la ficha completa). Si quien abre el
 * diálogo no puede leer esa ficha (403), se avisa «Sin acceso a la ficha»
 * en vez de romper.
 */

export const PENDIENTE = 'Pendiente de cumplimentar';

/** Tri-estado de la ficha (alergias, ácido hialurónico): true → el detalle
 *  (o «Sí» si no lo hay), false → «No», null → lo que haya escrito, y solo
 *  «Pendiente» si además no hay nada escrito.
 *
 *  Sin preguntar CON algo escrito se dice lo escrito (dirección, 2026-09-21),
 *  que es lo que ya hacen el parte del día y la hoja de anestesia: si nadie ha
 *  contestado la casilla, lo que hay apuntado es lo único que hay, y decir
 *  «Pendiente de cumplimentar» con un LATEX escrito al lado es esconderlo. */
function textoTriEstado(valor, detalle) {
  const escrito = (detalle || '').trim();
  if (valor === true) return escrito || 'Sí';
  if (valor === false) return 'No';
  return escrito || PENDIENTE;
}

/**
 * Alergias. Con la casilla en «no» y una alergia escrita al lado se lee la
 * alergia, con el mismo aviso y la misma frase que imprimen el parte del día y
 * la hoja de anestesia (2026-09-18). Recepción y el doctor, al dar o abrir la
 * cita de una paciente con el látex escrito en su ficha, leían «Alergias: No»:
 * el quirófano se enteraba y la consulta no.
 */
export function textoAlergias(p) {
  const contradice = textoQueContradice(p?.alergias, p?.alergias_text);
  if (contradice) return `${AVISO_REVISAR} ${contradice}`;
  return textoTriEstado(p?.alergias, p?.alergias_text);
}

export function textoMedicacion(p) {
  return (p?.medicacion_habitual || '').trim() || PENDIENTE;
}

/**
 * Ácido hialurónico previo: el mismo agujero que las alergias. Aquí NO se
 * pone el «REVISAR:» —esa es la frase de la casa para lo que frena un
 * quirófano, y el hialurónico previo no tiene todavía regla propia en ninguna
 * parte— pero tampoco se calla lo que hay escrito: se dicen las dos cosas,
 * con las mismas palabras que el informe clínico (`routers/informes_router`).
 */
export function textoAhPrevio(p) {
  const contradice = textoQueContradice(p?.ah_previo, p?.ah_previo_text);
  if (contradice) return `No — y aun así consta: ${contradice}`;
  return textoTriEstado(p?.ah_previo, p?.ah_previo_text);
}

/** Antecedentes médicos: SOLO el campo `antecedentes_medicos` de la ficha
 *  general. Nunca la historia general ni las notas de la importación
 *  (administración, 2026-09-09). */
export function textoAntecedentes(p) {
  return (p?.antecedentes_medicos || '').trim() || PENDIENTE;
}

function Dato({ etiqueta, valor, testId }) {
  const pendiente = valor === PENDIENTE;
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-32 shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground font-medium pt-0.5">
        {etiqueta}
      </span>
      <span
        className={`flex-1 min-w-0 whitespace-pre-wrap ${pendiente ? 'italic text-muted-foreground/80' : 'text-[#13231E]'}`}
        data-testid={testId}
      >
        {valor}
      </span>
    </div>
  );
}

export default function InfoClinicaPaciente({ patientId, testId = 'info-clinica', onFicha }) {
  // undefined = cargando · { ficha } = datos · { sinAcceso } = 403 · { error } = otro fallo
  const [estado, setEstado] = useState(undefined);
  // La ficha que ya se ha leído, también para quien la necesita al dar la
  // cita (paciente prohibido, 14/10/2026): así no se pide dos veces.
  const avisar = useRef(onFicha);
  avisar.current = onFicha;
  useEffect(() => {
    if (estado?.ficha) avisar.current?.(estado.ficha);
  }, [estado]);

  useEffect(() => {
    if (!patientId) {
      setEstado(undefined);
      return undefined;
    }
    // `vigente` evita pintar la ficha de un paciente anterior si se cambia de
    // paciente antes de que llegue la respuesta.
    let vigente = true;
    setEstado(undefined);
    api.get(`/api/patients/${patientId}`)
      .then((r) => {
        if (!vigente) return;
        const ficha = r?.data && !Array.isArray(r.data) ? r.data : {};
        setEstado({ ficha });
      })
      .catch((e) => {
        if (!vigente) return;
        setEstado(e?.response?.status === 403 ? { sinAcceso: true } : { error: true });
      });
    return () => { vigente = false; };
  }, [patientId]);

  let cuerpo;
  if (!patientId) {
    cuerpo = <div className="text-xs text-muted-foreground italic">Elige el paciente para ver sus datos clínicos.</div>;
  } else if (estado === undefined) {
    cuerpo = (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando la ficha…
      </div>
    );
  } else if (estado.sinAcceso) {
    cuerpo = <div className="text-xs text-amber-700" data-testid={`${testId}-estado`}>Sin acceso a la ficha</div>;
  } else if (estado.error) {
    cuerpo = <div className="text-xs text-red-600" data-testid={`${testId}-estado`}>No se pudo cargar la ficha del paciente</div>;
  } else {
    const p = estado.ficha;
    cuerpo = (
      <div className="space-y-1.5">
        <Dato etiqueta="Alergias" valor={textoAlergias(p)} testId={`${testId}-alergias`} />
        <Dato etiqueta="Medicación" valor={textoMedicacion(p)} testId={`${testId}-medicacion`} />
        <Dato etiqueta="Ác. hialurónico previo" valor={textoAhPrevio(p)} testId={`${testId}-ah-previo`} />
        <Dato etiqueta="Antecedentes médicos" valor={textoAntecedentes(p)} testId={`${testId}-antecedentes`} />
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.04)] px-3 py-2.5"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
        <HeartPulse className="w-3.5 h-3.5 text-[#2E6E5E]" /> Información clínica del paciente
      </div>
      {cuerpo}
    </div>
  );
}
